<template>
  <div>
    <BlBackdrop
      title="Edit schedule"
      label="Update"
      :disabled="isDisabledSave"
      @backdrop-close="$emit('close-modal')"
      @backdrop-action="onSave"
    >
      <div
        :class="{ 'overflow-y-auto': conflictedDates.length }"
        :style="{ width: '40rem', maxHeight: 'calc(100vh - 16rem)' }"
      >
        <div class="px-8 flex flex-col gap-4 mb-4">
          <div class="flex items-center gap-2">
            <BlIcon icon="user" color="slate-6" class="w-6 h-6" />
            <BlSelectBox
              class="max-w-136"
              v-model="therapistInput"
              :placeholder="
                therapistSelectLoading ? 'Loading ...' : 'Select therapist'
              "
              :options="therapistOptions"
              search
              @change="onChangeTherapist"
            />
          </div>
          <div class="flex items-center gap-2">
            <BlIcon icon="user-square" color="slate-6" class="w-6 h-6" />
            <BlSelectBox
              class="max-w-136"
              v-model="session.client_id"
              :options="[
                { value: session.client.id, label: session.client.name }
              ]"
              disabled
            />
          </div>
          <div class="flex items-center gap-2">
            <BlIcon icon="calendar-blank" color="slate-6" class="w-6 h-6" />
            <BlInputDate
              class="flex-grow"
              :input="startDateInput"
              @changed="onChangeDate"
            />
          </div>
          <div class="flex items-center gap-2">
            <BlIcon icon="clock-outline" color="slate-6" class="w-6 h-6" />
            <BlSelectTimeslot
              class="flex-grow"
              v-model="timeInput"
              :placeholder="
                timeSelectLoading ? 'Loading...' : 'Select timeslot'
              "
              :options="timeOptions"
              @change="onChangeTimeSlot"
            />
          </div>
          <div
            v-if="conflictedDates.length"
            class="rounded flex items-start gap-2 py-2 px-3 bg-tulip-100"
          >
            <BlIcon icon="warning" color="tulip-7" class="h-6 w-6" />
            <div class="flex flex-col w-128">
              <BlTextBody2 :text="suffixError" class="text-tulip-700" />
              <div
                v-for="date in conflictedDates"
                :key="date"
                class="flex items-center gap-2 px-2"
              >
                <div class="rounded h-1 w-1 bg-tulip-700"></div>
                <BlTextBody2 :text="date" class="text-tulip-700" />
              </div>
              <BlTextBody2
                text="Please adjust the details to proceed."
                class="text-tulip-700"
              />
            </div>
          </div>
        </div>
        <div class="w-full h-5 bg-grey-100 mb-4" />
        <div class="px-8 pb-8 mb-4">
          <div
            class="flex items-center mb-2 cursor-pointer"
            @click="setAssignSupervisorsInput = !setAssignSupervisorsInput"
          >
            <div :style="{ transform: 'scale(1.375)' }" class="pl-2 pr-4">
              <BlInputToggle
                :checked="setAssignSupervisorsInput"
                @changed="
                  setAssignSupervisorsInput = !setAssignSupervisorsInput
                "
              />
            </div>
            <BlTextSubtitle1 text="Assign supervisor" />
          </div>
          <div class="flex mb-4">
            <BlTextBody2
              :text="
                `If you assign a supervisor, they will find this session listed in the table view on their 'My Sessions' page.`
              "
            />
          </div>
          <div v-if="setAssignSupervisorsInput" class="flex">
            <BlSearchBox
              type="user"
              placeholder="Select therapist"
              v-model="assignSupervisorsInput"
              :options="assignSupervisorOptions"
              @change="assignSupervisorsInput = $event"
              @search="fetchSupervisors"
              :searching="assignSupervisorsSelectLoading"
              drop="up"
            />
          </div>
        </div>
        <div class="absolute left-8 -bottom-14 z-10">
          <BlInputButton
            type="plain-error"
            icon="trash"
            label="Remove schedule"
            @clicked="deleteOpened = true"
          />
        </div>
      </div>
    </BlBackdrop>
    <BlBackdrop
      v-if="deleteOpened"
      title="Remove schedule"
      description="You are about to unschedule this session. If you proceed, it will disappear from the assigned therapist's calendar."
      label="Confirm unschedule"
      type="error"
      :disabled="deleteLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onRemove"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { CLOCK_HALF } from "@/plugins/assets.js";
import moment from "moment";

import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlInputDate from "../../BlInputDate.vue";
import BlSelectTimeslot from "../../BlSelectTimeslot.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlSearchBox from "../../BlSearchBox.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "SessionEditScheduleModal",
  components: {
    BlTextSubtitle1,
    BlTextBody2,
    BlIcon,
    BlSelectBox,
    BlInputDate,
    BlSelectTimeslot,
    BlInputToggle,
    BlSearchBox,
    BlInputButton,
    BlBackdrop
  },
  props: {
    session: { type: Object, required: true }
  },
  data() {
    return {
      submitLoading: false,
      therapistSelectLoading: false,
      therapistInput: null,
      therapistOptions: [],
      startDateInput: { model: "" },
      timeSelectLoading: false,
      timeInput: [],
      setAssignSupervisorsInput: false,
      assignSupervisorsSelectLoading: false,
      assignSupervisorsInput: [],
      assignSupervisorOptions: [],
      conflictedDates: [],
      deleteOpened: false,
      deleteLoading: false,
      clockHalf: CLOCK_HALF
    };
  },
  computed: {
    timeOptions() {
      const arr = this.clockHalf
        .filter(i => {
          const h = i.split(":")[0];
          return Number(h) > 6 && Number(h) < 19;
        })
        .map(i => ({ value: i }));
      return arr;
    },
    isDisabledSave() {
      if (this.submitLoading) return true;
      if (!this.therapistInput) return true;
      if (!this.timeInput.length) return true;
      return false;
    },
    suffixError() {
      let text = "";
      const therapist = this.therapistOptions.find(
        i => i.value === this.therapistInput
      ) || { label: "Slot" };

      text = therapist.label;
      const start = this.timeInput[0];
      const idx = this.clockHalf.indexOf(
        this.timeInput[this.timeInput.length - 1]
      );
      const end = this.clockHalf[idx + 1];
      text += ` isn't available for the selected timeslot (${start} - ${end}) on the following day(s):`;

      return text;
    }
  },
  async mounted() {
    this.therapistInput = this.session.appointment.user_id;
    this.startDateInput.model = this.session.appointment.date;
    const startIdx = this.clockHalf.indexOf(
      this.session.appointment.start_time_string
    );
    const endIdx =
      this.clockHalf.indexOf(this.session.appointment.end_time_string) - 1;
    this.timeInput = this.clockHalf.filter(
      (_, idx) => idx >= startIdx && idx <= endIdx
    );
    if (this.session.appointment.supervisors.length) {
      this.setAssignSupervisorsInput = true;
      this.assignSupervisorsInput = this.session.appointment.supervisors.map(
        i => i.id
      );
    }

    this.therapistSelectLoading = true;
    this.assignSupervisorsSelectLoading = true;
    let params = `?sort=alphabetical&status=active&client_id=${this.session.client_id}`;
    this.getSupervisors({ params }).then(({ success, data }) => {
      this.therapistSelectLoading = false;
      this.assignSupervisorsSelectLoading = false;
      if (!success) return;
      this.therapistOptions = data.map(i => ({ value: i.id, label: i.name }));
      this.assignSupervisorOptions = data
        .map(i => ({ value: i.id, label: i.name }))
        .filter(i => i.value !== this.therapistInput);
    });
  },
  methods: {
    ...mapActions([
      "getSupervisors",
      "updateScheduleSession",
      "deleteScheduleSession"
    ]),
    displayDate(date, format) {
      if (!date) return "";
      return moment(date).format(format);
    },
    fetchSupervisors(val = "") {
      this.assignSupervisorsSelectLoading = true;
      let params = `?sort=alphabetical&status=active&client_id=${this.session.client_id}`;
      if (val) params += `&query=${val}`;
      this.getSupervisors({ params }).then(({ success, data }) => {
        this.assignSupervisorsSelectLoading = false;
        if (!success) return;
        this.assignSupervisorOptions = data
          .map(i => ({ value: i.id, label: i.name }))
          .filter(i => i.value !== this.therapistInput);
      });
    },
    onChangeTherapist(val) {
      this.therapistInput = val;
      this.assignSupervisorsInput = this.assignSupervisorsInput.filter(
        i => i !== val
      );
    },
    onChangeDate(payload) {
      this.startDateInput.model = payload.value;
    },
    onChangeTimeSlot(val) {
      this.timeInput = val;
    },
    onSave() {
      const start = this.timeInput[0];
      const idx = this.clockHalf.indexOf(
        this.timeInput[this.timeInput.length - 1]
      );
      const end = this.clockHalf[idx + 1];
      const data = {
        appointment_id: this.session.appointment.id,
        appointment: {
          user_id: this.therapistInput,
          date: this.startDateInput.model,
          start_time: start,
          end_time: end,
          supervisor_ids: this.setAssignSupervisorsInput
            ? this.assignSupervisorsInput
            : []
        }
      };

      this.conflictedDates = [];
      this.submitLoading = true;
      this.updateScheduleSession(data)
        .then(({ success, conflicted_dates: dates }) => {
          if (!success) {
            this.submitLoading = false;
            const format = "DD MMM YYYY";
            this.conflictedDates = dates
              .map(i => {
                if (typeof i === "object") {
                  for (let key in i) {
                    const d = moment(i[key], "YYYY-MM-DD").format(format);
                    return `${d}`;
                  }
                } else return moment(i, "YYYY-MM-DD").format(format);
              })
              .filter(i => i);
            return;
          }
          this.submitLoading = false;
          this.$emit("close-modal");
        })
        .catch(err => {
          this.submitLoading = false;
          console.error(err);
        });
    },
    onRemove() {
      const data = { id: this.session.id };

      this.conflictedAppointments = [];
      this.deleteLoading = true;
      this.deleteScheduleSession(data)
        .then(({ success }) => {
          this.deleteLoading = false;
          if (!success) return;
          this.$emit("close-modal");
        })
        .catch(err => {
          this.deleteLoading = false;
          console.error(err);
        });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/sessions/draft/EditScheduleModal.vue